import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Select, MenuItem, FormControl,
  InputLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, IconButton, Chip, CircularProgress, Alert, Divider, Tooltip,
  Tab, Tabs,
  useTheme, alpha,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Archive as ArchiveIcon, ArrowBack as BackIcon,
  FormatListBulleted as ListIcon, Upload as ImportIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { useVenue } from '../../../../context/VenueContext';
import wineListService from '../../../../services/wineListService';
import wineListItemService from '../../../../services/wineListItemService';
import inventoryItemService from '../../../../services/inventoryItemService';
import WineListVisibilityToggle from '../../../../components/venue/WineListVisibilityToggle';
import WineListSectionsPanel from '../../../../components/venue/WineListSectionsPanel';
import WineListFormatsPanel from '../../../../components/venue/WineListFormatsPanel';
import WineListPreviewPanel from '../../../../components/venue/WineListPreviewPanel';
import WineListPublishPanel from '../../../../components/venue/WineListPublishPanel';
import WineListSyncDialog from '../../../../components/venue/WineListSyncDialog';
import type { WineList, WineListItem, WineListItemVisibility, WineListStatus } from '../../../../types/venue';

type TabKey = 'wines' | 'sections' | 'formats' | 'preview';

const STATUS_COLORS: Record<WineListStatus, 'default' | 'primary' | 'success' | 'warning'> = {
  DRAFT: 'default',
  ACTIVE: 'success',
  ARCHIVED: 'warning',
};

const COLOR_DOT: Record<string, string> = {
  RED: '#922B21',
  WHITE: '#c9a227',
  PINK_ROSE: '#c2185b',
};

const COLOR_LABEL: Record<string, string> = {
  RED: 'Red',
  WHITE: 'White',
  PINK_ROSE: 'Rosé',
};

const thStyle = {
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'uppercase' as const,
};

const WineListDetail: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { venueId } = useVenue();
  const { wineListId } = useParams<{ wineListId: string }>();
  const base = `/admin/venues/${venueId}/wine-lists`;
  const itemBase = `${base}/${wineListId}`;

  // Inventory lookup maps — built once per venue load (stock only; size is now on WineListItem).
  // itemStockMap:  inventoryItemId → quantity  (for linked wine list items)
  // wineStockMap:  wineId → total quantity      (fallback for unlinked items)
  const [itemStockMap, setItemStockMap] = useState<Record<string, number>>({});
  const [wineStockMap, setWineStockMap] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!venueId) return;
    inventoryItemService.getItems(venueId, 1, 500)
      .then(result => {
        const iStock: Record<string, number> = {};
        const wStock: Record<string, number> = {};

        for (const inv of result.data) {
          const qty = inv.balance?.totalQuantity ?? 0;
          iStock[inv._id] = qty;
          if (inv.wineId) wStock[inv.wineId] = (wStock[inv.wineId] ?? 0) + qty;
        }

        setItemStockMap(iStock);
        setWineStockMap(wStock);
      })
      .catch(() => { /* best-effort; silent failure */ });
  }, [venueId]);

  // Wine list header
  const [wineList, setWineList] = useState<WineList | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');

  // Items
  const [items, setItems] = useState<WineListItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [itemsError, setItemsError] = useState('');
  const [visibilityError, setVisibilityError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 0, pageSize: 50, totalItems: 0 });

  // Active tab — Wines / Sections / Preview
  const [activeTab, setActiveTab] = useState<TabKey>('wines');

  // W8 — sync dialog open flag
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);

  // Load wine list header
  useEffect(() => {
    if (!wineListId) return;
    setListLoading(true);
    wineListService.getWineList(venueId, wineListId)
      .then(setWineList)
      .catch(() => setListError('Could not load this wine list. It may have been deleted or you may not have access.'))
      .finally(() => setListLoading(false));
  }, [venueId, wineListId]);

  // Load items
  const fetchItems = useCallback(async (q: string, visibility: string, page: number, pageSize: number) => {
    if (!wineListId) return;
    setItemsLoading(true);
    setItemsError('');
    try {
      const result = await wineListItemService.getItems(
        venueId, wineListId, page + 1, pageSize,
        q || undefined,
        visibility || undefined,
      );
      setItems(result.data ?? []);
      setPagination(prev => ({ ...prev, page, pageSize, totalItems: result.pagination.totalItems }));
    } catch (err) {
      console.error('Failed to load items', err);
      setItemsError('Could not load wines. Please try again.');
    } finally {
      setItemsLoading(false);
    }
  }, [venueId, wineListId]);

  const debouncedFetch = useCallback(
    debounce((q: string, vis: string, page: number, pageSize: number) => {
      fetchItems(q, vis, page, pageSize);
    }, 300),
    [fetchItems],
  );

  useEffect(() => {
    debouncedFetch(searchQuery, visibilityFilter, pagination.page, pagination.pageSize);
    return () => debouncedFetch.cancel();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, visibilityFilter, pagination.page, pagination.pageSize]);

  // Computed LIVE / HIDDEN counts from the current page
  const liveCounts = useMemo(() => {
    const live = items.filter(i => i.visibility === 'LIVE').length;
    const hidden = items.filter(i => i.visibility === 'HIDDEN').length;
    return { live, hidden };
  }, [items]);

  const handleVisibilityToggle = async (item: WineListItem, next: WineListItemVisibility) => {
    if (!wineListId) return;
    setVisibilityError('');
    try {
      const updated = await wineListItemService.updateItem(venueId, wineListId, item._id, { visibility: next });
      setItems(prev => prev.map(i => i._id === updated._id ? updated : i));
    } catch {
      setVisibilityError('Could not update visibility. Please try again.');
    }
  };

  const handleRemoveItem = async (e: React.MouseEvent, item: WineListItem) => {
    e.stopPropagation();
    const wineName = item.wineDisplay?.name ?? 'this wine';
    if (!window.confirm(`Remove "${wineName}" from the list?`)) return;
    if (!wineListId) return;
    try {
      await wineListItemService.deleteItem(venueId, wineListId, item._id);
      fetchItems(searchQuery, visibilityFilter, pagination.page, pagination.pageSize);
    } catch {
      setItemsError('Could not remove the wine. Please try again.');
    }
  };

  const handleArchiveList = async () => {
    if (!wineList || !wineListId) return;
    if (!window.confirm(`Archive "${wineList.name}"? Its wines will be preserved.`)) return;
    try {
      await wineListService.deleteWineList(venueId, wineListId);
      navigate(base);
    } catch {
      setListError('Could not archive this wine list. Please try again.');
    }
  };

  // Full-page error — list couldn't be loaded at all
  if (listError && !listLoading && !wineList) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(base)} sx={{ textTransform: 'none', color: theme.palette.text.secondary, alignSelf: 'flex-start' }}>
          Wine Lists
        </Button>
        <Alert severity="error">{listError}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {/* Wine list header card */}
      <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        {listLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">Loading wine list…</Typography>
          </Box>
        ) : wineList ? (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                <Button
                  startIcon={<BackIcon />}
                  onClick={() => navigate(base)}
                  sx={{ textTransform: 'none', color: theme.palette.text.secondary, p: 0, minWidth: 0, '&:hover': { background: 'none', color: theme.palette.primary.main } }}
                >
                  Wine Lists
                </Button>
                <Typography color="text.disabled" sx={{ lineHeight: 1 }}>/</Typography>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
                >
                  {wineList.name}
                </Typography>
                <Chip
                  label={wineList.status}
                  size="small"
                  color={STATUS_COLORS[wineList.status]}
                  sx={{ fontSize: '0.7rem', height: 22 }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>Type</Typography>
                  <Typography variant="body2">{wineList.type.replace(/_/g, ' ')}</Typography>
                </Box>
                {wineList.description && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>Description</Typography>
                    <Typography variant="body2">{wineList.description}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {wineList.status !== 'ARCHIVED' && (
                <Button startIcon={<ArchiveIcon />} color="warning" onClick={handleArchiveList} sx={{ textTransform: 'none' }}>
                  Archive
                </Button>
              )}
              <Button startIcon={<EditIcon />} variant="outlined" onClick={() => navigate(`${base}/edit/${wineListId}`)} sx={{ textTransform: 'none' }}>
                Edit
              </Button>
              {/* W8 — single Import button opens the import/update dialog,
                  which contains Download Template, Download Existing Items,
                  Choose File, Preview, and Confirm. Old separate Template
                  button removed; legacy /import wine-add route remains
                  reachable by direct URL but is no longer in the header. */}
              <Button startIcon={<ImportIcon />} variant="outlined" onClick={() => setSyncDialogOpen(true)} sx={{ textTransform: 'none' }}>
                Import
              </Button>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`${itemBase}/add`)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                Add Wine
              </Button>
            </Box>
          </Box>
        ) : null}

        {/* Non-fatal list error (e.g. archive failed) */}
        {listError && wineList && (
          <Alert severity="error" onClose={() => setListError('')} sx={{ mt: 2, borderRadius: 1 }}>{listError}</Alert>
        )}
      </Paper>

      {/* Public link / publish state — sits between the wine list header and the
          tabbed content so publish status is always visible without a click. */}
      {wineList && wineListId && (
        <WineListPublishPanel
          venueId={venueId}
          wineListId={wineListId}
          listStatus={wineList.status}
        />
      )}

      {/* Tabs: Wines / Sections / Preview */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Tabs
          value={activeTab}
          onChange={(_e, value: TabKey) => setActiveTab(value)}
          sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`, px: 2 }}
        >
          <Tab value="wines" label="Wines" sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab value="sections" label="Sections" sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab value="formats" label="Formats" sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab value="preview" label="Preview" sx={{ textTransform: 'none', fontWeight: 600 }} />
        </Tabs>

        {activeTab === 'sections' && wineListId && (
          <WineListSectionsPanel venueId={venueId} wineListId={wineListId} />
        )}

        {activeTab === 'formats' && wineListId && (
          <WineListFormatsPanel venueId={venueId} wineListId={wineListId} />
        )}

        {activeTab === 'preview' && wineListId && (
          <WineListPreviewPanel venueId={venueId} wineListId={wineListId} />
        )}

        {activeTab === 'wines' && (
        <>
        {/* Items toolbar */}
        <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search wines in list…"
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setPagination(p => ({ ...p, page: 0 })); }}
              sx={{ minWidth: 220 }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Visibility</InputLabel>
              <Select
                value={visibilityFilter}
                label="Visibility"
                onChange={e => { setVisibilityFilter(e.target.value); setPagination(p => ({ ...p, page: 0 })); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="LIVE">Live</MenuItem>
                <MenuItem value="HIDDEN">Hidden</MenuItem>
              </Select>
            </FormControl>
          </Box>
          {/* Summary counts from current page */}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            {!itemsLoading && items.length > 0 && (
              <>
                <Typography variant="caption" color="text.secondary">
                  <Box component="span" sx={{ color: '#1a7f37', fontWeight: 600 }}>{liveCounts.live}</Box> live
                </Typography>
                <Typography variant="caption" color="text.disabled">·</Typography>
                <Typography variant="caption" color="text.secondary">
                  <Box component="span" sx={{ fontWeight: 600 }}>{liveCounts.hidden}</Box> hidden
                </Typography>
                <Typography variant="caption" color="text.disabled">·</Typography>
              </>
            )}
            <Typography variant="caption" color="text.secondary">
              {pagination.totalItems} {pagination.totalItems === 1 ? 'wine' : 'wines'} total
            </Typography>
          </Box>
        </Box>

        {/* Inline errors */}
        {visibilityError && (
          <Box sx={{ px: 3, pt: 1.5 }}>
            <Alert severity="error" onClose={() => setVisibilityError('')} sx={{ borderRadius: 1 }}>{visibilityError}</Alert>
          </Box>
        )}
        {itemsError && (
          <Box sx={{ px: 3, pt: 1.5 }}>
            <Alert severity="error" onClose={() => setItemsError('')} sx={{ borderRadius: 1 }}>{itemsError}</Alert>
          </Box>
        )}

        <TableContainer sx={{ overflowX: 'auto' }}>
          {/* minWidth keeps the column widths intact on tablet — without it
              MUI's auto-table-layout would squash narrow columns. Total of
              the per-column widths below is ~1028px; 1100 gives a small
              padding allowance and forces horizontal scroll under that. */}
          <Table size="small" sx={{ minWidth: 1100 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary, minWidth: 180 }}>Wine</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary, minWidth: 140 }}>Producer</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary, width: 72 }}>Vintage</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary, width: 60 }}>Color</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary, width: 80 }}>Bin</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary, width: 90 }}>Size</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary, width: 100 }}>Price</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary, width: 90 }}>Visibility</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary, width: 80 }}>Source</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary, width: 60 }}>Stock</TableCell>
                <TableCell sx={{ width: 76 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {itemsLoading ? (
                <TableRow>
                  <TableCell colSpan={11}>
                    <Box sx={{ py: 5, display: 'flex', justifyContent: 'center', gap: 2, alignItems: 'center' }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" color="text.secondary">Loading wines…</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11}>
                    <Box sx={{ py: 7, textAlign: 'center', color: theme.palette.text.secondary }}>
                      <ListIcon sx={{ fontSize: 44, mb: 1.5, opacity: 0.25 }} />
                      <Typography variant="body2" fontWeight={500} gutterBottom>
                        {searchQuery || visibilityFilter
                          ? 'No wines match the current filters.'
                          : 'No wines on this list yet.'}
                      </Typography>
                      {!searchQuery && !visibilityFilter && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => navigate(`${itemBase}/add`)}
                          sx={{ mt: 1, textTransform: 'none' }}
                        >
                          Add first wine
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                items.map(item => {
                  const display = item.wineDisplay;
                  const colorDot = display?.color ? COLOR_DOT[display.color] : null;
                  const colorLabel = display?.color ? (COLOR_LABEL[display.color] ?? display.color) : null;
                  const priceDisplay = item.listPrice != null
                    ? `${item.currency ? `${item.currency} ` : ''}${item.listPrice.toFixed(2)}`
                    : null;

                  return (
                    <TableRow
                      key={item._id}
                      hover
                      onClick={() => navigate(`${itemBase}/edit/${item._id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      {/* Wine name — truncate with tooltip */}
                      <TableCell sx={{ maxWidth: 260 }}>
                        {(() => {
                          const name = display?.name;
                          const size = item.bottleSize != null
                            ? (item.bottleSize >= 1000 ? `${item.bottleSize / 1000}L` : `${item.bottleSize}ml`)
                            : null;
                          const fullLabel = name ? (size ? `${name} · ${size}` : name) : null;
                          return (
                            <Tooltip title={fullLabel ?? ''} placement="top" disableHoverListener={!fullLabel || fullLabel.length < 36}>
                              <Typography variant="body2" fontWeight={500} noWrap>
                                {name
                                  ? <>{name}{size && <Box component="span" sx={{ color: theme.palette.text.secondary, fontWeight: 400, ml: 0.5 }}>· {size}</Box>}</>
                                  : <Box component="span" sx={{ color: theme.palette.text.disabled, fontStyle: 'italic' }}>Unknown wine</Box>
                                }
                              </Typography>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>

                      {/* Producer */}
                      <TableCell sx={{ maxWidth: 160 }}>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {display?.producerName ?? '—'}
                        </Typography>
                      </TableCell>

                      {/* Vintage */}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {display?.displayVintage ?? '—'}
                        </Typography>
                      </TableCell>

                      {/* Color — dot + label */}
                      <TableCell>
                        {colorDot ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, backgroundColor: colorDot }} />
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
                              {colorLabel}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>

                      {/* Bin */}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: item.binNumber ? theme.palette.text.primary : theme.palette.text.disabled }}>
                          {item.binNumber || '—'}
                        </Typography>
                      </TableCell>

                      {/* Size — own field on the wine list item */}
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                          {item.bottleSize != null ? `${item.bottleSize} ml` : '—'}
                        </Typography>
                      </TableCell>

                      {/* Price */}
                      <TableCell>
                        {priceDisplay ? (
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                            {priceDisplay}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>

                      {/* Visibility toggle — inline, stops row click */}
                      <TableCell onClick={e => e.stopPropagation()}>
                        <WineListVisibilityToggle
                          visibility={item.visibility}
                          onChange={next => handleVisibilityToggle(item, next)}
                        />
                      </TableCell>

                      {/* Source */}
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                          {item.source ?? '—'}
                        </Typography>
                      </TableCell>

                      {/* Stock — from linked inventory item, or fallback aggregation */}
                      <TableCell>
                        {(() => {
                          const qty = item.inventoryItemId
                            ? itemStockMap[item.inventoryItemId]
                            : wineStockMap[item.wineId];
                          return qty != null ? (
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{ fontFamily: 'monospace', fontSize: '0.82rem', color: qty > 0 ? '#1a7f37' : theme.palette.text.disabled }}
                            >
                              {qty}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          );
                        })()}
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right" onClick={e => e.stopPropagation()} sx={{ whiteSpace: 'nowrap' }}>
                        <IconButton size="small" onClick={() => navigate(`${itemBase}/edit/${item._id}`)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={e => handleRemoveItem(e, item)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider />
        <TablePagination
          component="div"
          count={pagination.totalItems}
          page={pagination.page}
          rowsPerPage={pagination.pageSize}
          onPageChange={(_e, newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
          onRowsPerPageChange={e => setPagination(prev => ({ ...prev, pageSize: parseInt(e.target.value, 10), page: 0 }))}
          rowsPerPageOptions={[25, 50, 100]}
        />
        </>
        )}
      </Paper>

      {/* W8 — sync items dialog */}
      {wineListId && (
        <WineListSyncDialog
          open={syncDialogOpen}
          onClose={() => setSyncDialogOpen(false)}
          onSuccess={() => fetchItems(searchQuery, visibilityFilter, pagination.page, pagination.pageSize)}
          venueId={venueId}
          wineListId={wineListId}
        />
      )}
    </Box>
  );
};

export default WineListDetail;
